<template>
  <PayPlanSmallCard
    v-if="cardProps"
    v-bind="cardProps"
    :has-previous="hasPrevious"
    :has-next="hasNext"
    :blocked="blocked"
    @previous-click="previous"
    @next-click="next"
    @add-click="emit('add-click', selected)"
    @close-click="emit('close-click', selected)"
    @plan-click="emit('plan-click', selected)"
    @blocked-open="emit('blocked-open', $event)"
  />
</template>

<script setup>
import { computed, ref, watch } from "vue";
import PayPlanSmallCard from "@components/PayPlanSmallCard/PayPlanSmallCard.vue";

/**
 * The "Current Pay Plan" cell slider (Eric, 2026-10-15): the card's ‹ › arrows
 * step through the employee's plan versions, each rendered in its own period
 * state colour (green current / blue future / grey lapsed). Clicking the card
 * re-emits `plan-click` with the version being shown, so the page can open that
 * version's drawer.
 */
const props = defineProps({
  /** Mapped plan versions, oldest → newest (see `mapPlanVersion`). */
  versions: {
    type: Array,
    default: () => [],
  },
  /** ER-12691: API-reported block; the "Add new Pay Plan" card shows "Not allowed" instead of +. */
  blocked: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits([
  "add-click",
  "close-click",
  "plan-click",
  "version-change",
  "blocked-open",
]);

/** Start on the version covering today, else the newest. */
function defaultIndex(list) {
  const current = (list || []).findIndex((version) => version?.state === "current");
  if (current !== -1) return current;
  return Math.max(0, (list || []).length - 1);
}

const index = ref(defaultIndex(props.versions));

// Reset when the *set* of versions or their period states change (a new as-of
// date re-resolves which version is current) — a plain re-render (filter,
// hover, …) must not lose the position the admin slid to.
watch(
  () =>
    (props.versions || []).map((version) => `${version?.versionId}:${version?.state}`).join(","),
  () => {
    index.value = defaultIndex(props.versions);
  }
);

const selected = computed(() => props.versions[index.value] || null);
const hasPrevious = computed(() => index.value > 0);
const hasNext = computed(() => index.value < props.versions.length - 1);

/** Only the card's own props — `versionId` / `status` stay on the slider. */
const cardProps = computed(() => {
  const version = selected.value;
  if (!version) return null;
  return {
    variant: version.variant,
    state: version.state,
    amountFormatted: version.amountFormatted,
    basisLabel: version.basisLabel,
    startDate: version.startDate,
    endDate: version.endDate,
    planReference: version.planReference,
    versionLabel: version.versionLabel,
  };
});

/**
 * Step ‹ / ›. `version-change` moves the page's As of date to the version, so it
 * is not emitted for the "Add new Pay Plan" placeholder (it has no dates), nor
 * when leaving it: the lapsed plan stays reachable with › back to the placeholder.
 */
function move(delta) {
  const from = selected.value;
  index.value += delta;
  if (from?.isAddPlaceholder || selected.value?.isAddPlaceholder) return;
  emit("version-change", selected.value);
}

function previous() {
  if (!hasPrevious.value) return;
  move(-1);
}

function next() {
  if (!hasNext.value) return;
  move(1);
}

defineExpose({ index, selected });
</script>
